<template>
  <div class="anamnesis-subset">
    <!-- Toolbar réutilisable -->
    <SubsetToolbar
      :sort-options="sortOptions"
      :sort-by="sortBy"
      :sort-direction="sortDirection"
      :is-card-view="isCardView"
      :search-query="searchQuery"
      search-placeholder="Rechercher un lieu..."
      create-button-text="Nouveau lieu"
      :show-create-button="true"
      :show-mobile-menu="showMobileMenu"
      @update:sort-by="handleUpdateSortBy"
      @update:search-query="handleUpdateSearchQuery"
      @toggle-sort-direction="handleToggleSortDirection"
      @toggle-view="handleToggleView"
      @toggle-mobile-menu="handleToggleMobileMenu"
      @create="$emit('create')"
    >
      <!-- Slot filtres : Filtres spécifiques aux lieux -->
      <template #filters>
        <!-- Filtre orphelins -->
        <button
          class="px-3 py-1.5 rounded-full text-sm transition-colors duration-200 flex items-center space-x-1.5"
          :class="[
            showOrphans
              ? 'bg-orange-100 text-orange-700 hover:bg-orange-200'
              : 'bg-gray-50 text-gray-600 hover:bg-gray-100'
          ]"
          @click="handleToggleOrphanFilter"
        >
          <UnlinkIcon class="h-4 w-4" />
          <span>Sans mémos</span>
        </button>

        <!-- Filtre Public/Privé - Bouton 3 états -->
        <button
          class="px-3 py-1.5 rounded-full text-sm transition-colors duration-200 flex items-center space-x-1.5"
          :class="[
            visibilityFilter === 'all'
              ? 'bg-gray-50 text-gray-600 hover:bg-gray-100'
              : visibilityFilter === 'public'
              ? 'bg-green-100 text-green-700 hover:bg-green-200'
              : 'bg-gray-100 text-gray-700 hover:bg-gray-200'
          ]"
          @click="cycleVisibilityFilter"
        >
          <EyeIcon v-if="visibilityFilter === 'all'" class="h-4 w-4" />
          <EyeIcon v-else-if="visibilityFilter === 'public'" class="h-4 w-4" />
          <EyeOffIcon v-else class="h-4 w-4" />
          <span>
            {{ visibilityFilter === 'all' ? 'Tous' : visibilityFilter === 'public' ? 'Publics' : 'Privés' }}
          </span>
        </button>

        <!-- Filtre Confidentiel - Bouton 3 états -->
        <button
          class="px-3 py-1.5 rounded-full text-sm transition-colors duration-200 flex items-center space-x-1.5"
          :class="[
            confidentialityFilter === 'all'
              ? 'bg-gray-50 text-gray-600 hover:bg-gray-100'
              : confidentialityFilter === 'confidential'
              ? 'bg-yellow-100 text-yellow-700 hover:bg-yellow-200'
              : 'bg-gray-100 text-gray-700 hover:bg-gray-200'
          ]"
          @click="cycleConfidentialityFilter"
        >
          <LockIcon v-if="confidentialityFilter === 'all'" class="h-4 w-4" />
          <LockIcon v-else-if="confidentialityFilter === 'confidential'" class="h-4 w-4" />
          <UnlockIcon v-else class="h-4 w-4" />
          <span>
            {{ confidentialityFilter === 'all' ? 'Tous' : confidentialityFilter === 'confidential' ? 'Confidentiels' : 'Non confidentiels' }}
          </span>
        </button>
      </template>

      <!-- Slot mobile-content -->
      <template #mobile-content>
        <div class="space-y-2">
          <div class="text-sm font-medium text-gray-700">Filtres mobiles</div>
          <!-- Version mobile des filtres si nécessaire -->
        </div>
      </template>
    </SubsetToolbar>

    <!-- Liste des locations -->
    <div v-if="paginatedLocations.length > 0" :class="{ 'anamnesis-grid': isCardView, 'anamnesis-table': !isCardView }">
      <!-- En-tête du tableau (mode table uniquement) -->
      <div v-if="!isCardView" class="bg-gray-50 px-4 py-3 border-b border-gray-200 flex items-center text-sm font-medium text-gray-700">
        <div class="flex-[2]">Lieu</div>
        <div class="flex-1 px-3">Adresse</div>
        <div class="flex-1 px-3">Ville</div>
        <div class="px-3">Statistiques</div>
        <div class="ml-auto">Actions</div>
      </div>
      
      <LocationCard
        v-for="location in paginatedLocations"
        :key="location.uuid"
        :location="location"
        :view-mode="isCardView ? 'default' : 'table'"
        :show-actions="showActions"
        :show-edit-button="showEditButton"
        :show-delete-button="showDeleteButton"
        :is-owned="isLocationOwned(location)"
        :is-public="location.is_public"
        :show-ownership-badge="true"
        :user-id="location.user_id"
        @edit="$emit('edit', $event)"
        @delete="$emit('delete', $event)"
        @add="$emit('create')"
      />
    </div>

    <div v-else class="text-center py-8">
      <p class="text-gray-500">Aucun lieu trouvé</p>
    </div>

    <!-- Compteur de locations -->
    <div class="flex justify-end mt-4 mb-2">
      <p class="text-sm text-gray-400">
        {{ filteredLocations.length }} lieu{{ filteredLocations.length > 1 ? 'x' : '' }} trouvé{{ filteredLocations.length > 1 ? 's' : '' }}
      </p>
    </div>

    <!-- Pagination -->
    <div v-if="filteredLocations.length > itemsPerPage" class="mt-4 flex justify-center space-x-2">
      <button
        :disabled="currentPage === 1"
        class="px-3 py-1 rounded-md border"
        :class="currentPage === 1 ? 'text-gray-400 border-gray-200' : 'text-gray-700 border-gray-300 hover:bg-gray-50'"
        @click="handlePreviousPage"
      >
        Précédent
      </button>
      <button
        v-for="page in totalPages"
        :key="page"
        class="px-3 py-1 rounded-md border"
        :class="currentPage === page ? 'bg-blue-500 text-white border-blue-500' : 'text-gray-700 border-gray-300 hover:bg-gray-50'"
        @click="currentPage = page"
      >
        {{ page }}
      </button>
      <button
        :disabled="currentPage === totalPages"
        class="px-3 py-1 rounded-md border"
        :class="currentPage === totalPages ? 'text-gray-400 border-gray-200' : 'text-gray-700 border-gray-300 hover:bg-gray-50'"
        @click="handleNextPage"
      >
        Suivant
      </button>
    </div>
  </div>
</template>

<script setup lang="ts">
import {
    Eye as EyeIcon,
    EyeOff as EyeOffIcon,
    Lock as LockIcon,
    Unlink as UnlinkIcon,
    Unlock as UnlockIcon
} from 'lucide-vue-next';
import type { ComputedRef, Ref } from 'vue';
import { computed, ref, watch } from 'vue';
import type { LocationWithMemos } from '../../api/types/entities/location/relations';
import { useAuthStore } from '../../stores/auth';
import type { SortOption } from '../UI/SubsetToolbar.vue';
import SubsetToolbar from '../UI/SubsetToolbar.vue';
import LocationCard from '../cards/LocationCard.vue';

// Types stricts
type SortBy = 'date' | 'site' | 'place' | 'country' | 'memos';
type SortDirection = 'asc' | 'desc';

// Props avec types stricts
interface Props {
  locations: LocationWithMemos[];
  showActions?: boolean;
  showEditButton?: boolean;
  showDeleteButton?: boolean;
  itemsPerPage?: number;
  defaultSort?: SortBy;
}

const props = withDefaults(defineProps<Props>(), {
  showActions: true,
  showEditButton: true,
  showDeleteButton: true,
  itemsPerPage: 20,
  defaultSort: 'date',
});

// Events avec types stricts
defineEmits<{
  create: [];
  edit: [location: LocationWithMemos];
  delete: [location: LocationWithMemos];
}>();

// Store auth
const authStore = useAuthStore();

// État réactif avec types stricts
const isCardView: Ref<boolean> = ref(true);
const searchQuery: Ref<string> = ref('');
const sortBy: Ref<SortBy> = ref(props.defaultSort);
const sortDirection: Ref<SortDirection> = ref('asc');
const showOrphans: Ref<boolean> = ref(false);
const currentPage: Ref<number> = ref(1);
const showMobileMenu: Ref<boolean> = ref(false);

// Filtres supplémentaires pour les locations
const visibilityFilter: Ref<'all' | 'public' | 'private'> = ref('all');
const confidentialityFilter: Ref<'all' | 'confidential' | 'not-confidential'> = ref('all');

// Options de tri avec types stricts
const sortOptions: ComputedRef<SortOption[]> = computed(() => [
  { value: 'date', label: 'Date' },
  { value: 'site', label: 'Nom du site' },
  { value: 'place', label: 'Ville' },
  { value: 'country', label: 'Pays' },
  { value: 'memos', label: 'Nombre de mémos' },
]);

// Handlers avec types de retour explicites
const handleUpdateSortBy = (value: string): void => {
  sortBy.value = value as SortBy;
};

const handleUpdateSearchQuery = (value: string): void => {
  searchQuery.value = value;
};

const handleToggleSortDirection = (): void => {
  sortDirection.value = sortDirection.value === 'asc' ? 'desc' : 'asc';
};

const handleToggleView = (): void => {
  isCardView.value = !isCardView.value;
};

const handleToggleOrphanFilter = (): void => {
  showOrphans.value = !showOrphans.value;
};

const handleToggleMobileMenu = (): void => {
  showMobileMenu.value = !showMobileMenu.value;
};

// Handlers pour les filtres supplémentaires
const cycleVisibilityFilter = (): void => {
  console.log('🔄 Cycle visibilité - Avant:', visibilityFilter.value);
  if (visibilityFilter.value === 'all') {
    visibilityFilter.value = 'public';
  } else if (visibilityFilter.value === 'public') {
    visibilityFilter.value = 'private';
  } else {
    visibilityFilter.value = 'all';
  }
  console.log('🔄 Cycle visibilité - Après:', visibilityFilter.value);
};

const cycleConfidentialityFilter = (): void => {
  console.log('🔄 Cycle confidentialité - Avant:', confidentialityFilter.value);
  if (confidentialityFilter.value === 'all') {
    confidentialityFilter.value = 'confidential';
  } else if (confidentialityFilter.value === 'confidential') {
    confidentialityFilter.value = 'not-confidential';
  } else {
    confidentialityFilter.value = 'all';
  }
  console.log('🔄 Cycle confidentialité - Après:', confidentialityFilter.value);
};

const handlePreviousPage = (): void => {
  if (currentPage.value > 1) {
    currentPage.value--;
  }
};

const handleNextPage = (): void => {
  if (currentPage.value < totalPages.value) {
    currentPage.value++;
  }
};

// Fonction pour vérifier la propriété d'une location
const isLocationOwned = (location: LocationWithMemos): boolean => {
  return authStore.isAssetOwner(location);
};

// Fonction de recherche typée correctement
const searchInLocation = (location: LocationWithMemos, query: string): boolean => {
  const searchTerm = query.toLowerCase();
  
  // Recherche dans les champs principaux
  if (location.site?.toLowerCase().includes(searchTerm)) return true;
  if (location.place?.toLowerCase().includes(searchTerm)) return true;
  if (location.country?.toLowerCase().includes(searchTerm)) return true;
  if (location.address?.toLowerCase().includes(searchTerm)) return true;
  if (location.post_code?.toLowerCase().includes(searchTerm)) return true;
  
  // Recherche dans l'UUID
  if (location.uuid?.toLowerCase().includes(searchTerm)) return true;
  
  return false;
};

// Filtrage et tri avec types stricts
const filteredLocations: ComputedRef<LocationWithMemos[]> = computed(() => {
  let result: LocationWithMemos[] = [...props.locations];

  console.log('🔍 Filtrage - État initial:', result.length, 'locations');

  // Filtrage par orphelins (sans mémos)
  if (showOrphans.value) {
    result = result.filter((location: LocationWithMemos): boolean => 
      !location.memos || location.memos.length === 0
    );
    console.log('🔍 Filtrage orphelins:', result.length, 'locations restantes');
  }

  // Filtrage par visibilité (public/privé)
  if (visibilityFilter.value !== 'all') {
    result = result.filter((location: LocationWithMemos): boolean => {
      if (visibilityFilter.value === 'public') {
        return location.is_public === true;
      } else if (visibilityFilter.value === 'private') {
        return location.is_public === false;
      }
      return true;
    });
    console.log('🔍 Filtrage visibilité:', visibilityFilter.value, '-', result.length, 'locations restantes');
  }

  // Filtrage par confidentialité
  if (confidentialityFilter.value !== 'all') {
    result = result.filter((location: LocationWithMemos): boolean => {
      if (confidentialityFilter.value === 'confidential') {
        return location.is_confidential === true;
      } else if (confidentialityFilter.value === 'not-confidential') {
        return location.is_confidential === false;
      }
      return true;
    });
    console.log('🔍 Filtrage confidentialité:', confidentialityFilter.value, '-', result.length, 'locations restantes');
  }

  // Filtrage par recherche
  if (searchQuery.value) {
    result = result.filter((location: LocationWithMemos): boolean => 
      searchInLocation(location, searchQuery.value)
    );
    console.log('🔍 Filtrage recherche:', result.length, 'locations restantes');
  }

  // Tri
  result.sort((a: LocationWithMemos, b: LocationWithMemos): number => {
    let comparison: number = 0;
    
    switch (sortBy.value) {
      case 'date':
        comparison = new Date(b.created_at || 0).getTime() - new Date(a.created_at || 0).getTime();
        break;
      case 'site':
        comparison = (a.site || '').localeCompare(b.site || '');
        break;
      case 'place':
        comparison = (a.place || '').localeCompare(b.place || '');
        break;
      case 'country':
        comparison = (a.country || '').localeCompare(b.country || '');
        break;
      case 'memos': {
        const aMemos = a.memos?.length || 0;
        const bMemos = b.memos?.length || 0;
        comparison = aMemos - bMemos;
        break;
      }
    }
    
    return sortDirection.value === 'asc' ? comparison : -comparison;
  });

  return result;
});

// Pagination avec types stricts
const totalPages: ComputedRef<number> = computed(() => 
  Math.ceil(filteredLocations.value.length / props.itemsPerPage)
);

const paginatedLocations: ComputedRef<LocationWithMemos[]> = computed(() => {
  const start: number = (currentPage.value - 1) * props.itemsPerPage;
  const end: number = start + props.itemsPerPage;
  return filteredLocations.value.slice(start, end);
});

// Watchers avec types stricts
watch([searchQuery, showOrphans, visibilityFilter, confidentialityFilter, sortBy], (): void => {
  currentPage.value = 1;
});
</script>

<style scoped>
.anamnesis-grid {
  display: grid;
  grid-template-columns: repeat(auto-fill, minmax(300px, 1fr));
  gap: 1rem;
}

.anamnesis-table {
  display: flex;
  flex-direction: column;
  gap: 0.5rem;
}
</style> 